import type { AdminHotel, AdminUser, ChannexUsageReport } from '@/lib/api/types';

/**
 * Organizations, assembled from what the API does give.
 *
 * There is no `/admin/organizations` yet (the backend team has said so), but
 * every hotel and every membership carries an `organizationId`, and the
 * Channex report names the organizations it bills. Grouping the three gives
 * a workable directory: who owns what, how big they are, where the trouble
 * is. A name is known only when Channex has seen the organization; the rest
 * are shown by id and by their owner.
 */
export interface OrgSummary {
  id: string;
  /** From the Channex report, when it has this organization. */
  name: string | null;
  plan: string | null;
  owner: AdminUser | null;
  hotels: AdminHotel[];
  users: AdminUser[];
  rooms: number;
  channels: number;
  activeHotels: number;
  syncErrors: number;
  /** Earliest hotel or membership — the closest thing to "joined". */
  since: string | null;
}

export function deriveOrganizations(hotels: AdminHotel[], users: AdminUser[], channex?: ChannexUsageReport | null): OrgSummary[] {
  const byId = new Map<string, OrgSummary>();
  const get = (id: string): OrgSummary => {
    let org = byId.get(id);
    if (!org) {
      org = { id, name: null, plan: null, owner: null, hotels: [], users: [], rooms: 0, channels: 0, activeHotels: 0, syncErrors: 0, since: null };
      byId.set(id, org);
    }
    return org;
  };
  const earlier = (a: string | null, b: string) => (a === null || b < a ? b : a);

  for (const h of hotels) {
    const org = get(h.organizationId);
    org.hotels.push(h);
    org.rooms += h.totalRooms;
    org.channels += h.connectedChannels;
    if (h.isActive) org.activeHotels += 1;
    if (h.syncError) org.syncErrors += 1;
    org.since = earlier(org.since, h.createdAt);
  }
  for (const u of users) {
    if (!u.organizationId) continue;
    const org = get(u.organizationId);
    org.users.push(u);
    if (u.role === 'owner' && !org.owner) org.owner = u;
    org.since = earlier(org.since, u.createdAt);
  }
  for (const o of channex?.organizations ?? []) {
    const org = get(o.organizationId);
    org.name = o.name;
    org.plan = o.plan;
  }
  return [...byId.values()].sort((a, b) => b.hotels.length - a.hotels.length || b.users.length - a.users.length);
}

/** What to call an organization in a cell: its name, or its owner's, or its id. */
export function orgLabel(org: OrgSummary): string {
  return org.name ?? (org.owner ? `Орг. ${org.owner.displayName || org.owner.email}` : `Организация ${org.id.slice(0, 8)}`);
}
